import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faTrash,
  faUsers,
  faChartLine,
  faExclamationTriangle,
  faExchangeAlt,
  faShieldAlt,
  faCheckCircle,
  faClipboardList,
  faHandshake,
  faEnvelope,
  faCheck,
  faCog,
  faBook,
  faLayerGroup,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { getToneColors } from '@learnzapp/core/theme';

// Base Modal Component
export const BaseModal = ({ isOpen, onClose, children, theme, maxWidth = '800px' }) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2.5rem',
      maxWidth: maxWidth,
      maxHeight: '90vh',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
      overflowY: 'auto',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {onClose && (
              <button
                style={styles.closeButton}
                onClick={onClose}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Delete Test Modal
export const DeleteTestModal = ({ isOpen, onClose, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.dashboard?.status?.incorrect}20, ${theme?.dashboard?.status?.incorrect}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.dashboard?.status?.incorrect,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    deleteButton: {
      background: theme?.dashboard?.status?.incorrect,
      color: 'white',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -5, 5, 0],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faTrash} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>Delete Test Result?</h2>
      <p style={styles.description}>
        This removes the test from your History only and cannot be undone. Your overall
        progress and readiness score are based on every question you've answered and won't
        change.
      </p>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.deleteButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          Delete
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Test Review Community Modal
export const TestReviewCommunity = ({ isOpen, onClose, communityStats, theme, type }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${communityStats.color}20, ${communityStats.color}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: communityStats.color,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    statsContainer: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '1rem',
      padding: '1.5rem',
      marginBottom: '2rem',
    },
    statRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    statLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    statValue: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    message: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.75rem',
      padding: '1rem',
      textAlign: 'center',
      fontSize: '0.875rem',
      color: communityStats.color,
      fontWeight: '500',
      border: `1px solid ${communityStats.color}30`,
    },
    closeButton: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]}, ${theme?.primary?.blue?.[600]})`,
      color: 'white',
      display: 'block',
      margin: '0 auto',
      transition: 'all 0.3s ease',
    },
  };

  const getDifficultyText = () => {
    switch (communityStats.level) {
      case 'easy':
        return `Easy ${type}`;
      case 'medium':
        return `Moderate ${type}`;
      case 'hard':
        return `Challenging ${type}`;
      default:
        return `Challenging ${type}`;
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          scale: [1, 1.1, 1],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
        }}
      >
        <FontAwesomeIcon icon={faUsers} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>{type} Community Performance</h2>
      <p style={styles.subtitle}>
        See how other learners performed on this {type}
      </p>
      
      <div style={styles.statsContainer}>
        <div style={styles.statRow}>
          <div style={styles.statLabel}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Difficulty Level</span>
          </div>
          <div style={{ ...styles.statValue, color: communityStats.color }}>
            {getDifficultyText()}
          </div>
        </div>
      </div>
      
      <div style={styles.message}>
        {communityStats.message}
      </div>


    </BaseModal>
  );
};

/**
 * Diagnostic results "How You Compare" modal. All copy/rows come from core
 * buildCommunityComparisonCopy so web + mobile stay byte-identical; this
 * component only renders. Difficulty tone maps via getToneColors.
 */
export const DiagnosticCommunityModal = ({ isOpen, onClose, copy, theme }) => {
  const accent = theme?.primary?.blue?.[500];
  const difficultyColor = copy?.difficulty
    ? getToneColors(theme, copy.difficulty.tone).color
    : undefined;

  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${accent}20, ${accent}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: accent,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    statsContainer: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '1rem',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    statRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    statValue: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    comparisonLine: {
      textAlign: 'center',
      fontSize: '0.9rem',
      color: theme?.text?.secondary,
      lineHeight: 1.5,
    },
  };

  if (!copy) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme} maxWidth="480px">
      <motion.div
        style={styles.iconContainer}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <FontAwesomeIcon icon={faUsers} style={styles.icon} />
      </motion.div>

      <h2 style={styles.title}>{copy.title}</h2>
      <p style={styles.subtitle}>{copy.subtitle}</p>

      <div style={styles.statsContainer}>
        {copy.rows.map((row, index) => (
          <div
            key={row.key}
            style={{
              ...styles.statRow,
              ...(index === copy.rows.length - 1 ? { marginBottom: 0 } : {}),
            }}
          >
            <span style={styles.statLabel}>{row.label}</span>
            <span
              style={{
                ...styles.statValue,
                ...(row.key === 'difficulty' && difficultyColor ? { color: difficultyColor } : {}),
              }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div style={styles.comparisonLine}>{copy.comparisonLine}</div>
    </BaseModal>
  );
};

// Switch Popup Modal
export const SwitchPopup = ({ isOpen, onClose, onConfirm, title, description, confirmText = "Yes", cancelText = "No", theme }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.semantic?.warning}20, ${theme?.semantic?.warning}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.semantic?.warning,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    confirmButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]}, ${theme?.primary?.blue?.[600]})`,
      color: 'white',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          scale: [1, 1.1, 1],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
        }}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>{title}</h2>
      {description && (
        <p style={styles.description}>
          {description}
        </p>
      )}
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          {cancelText}
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.confirmButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          {confirmText}
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Change Exam Modal
export const ChangeExamModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Change Certificate", 
  description = "Select a different certificate to switch to",
  bundles = [],
  selectedBundle,
  onBundleSelect,
  theme,
  isLoading = false,
  progress = 0,
  currentStep = 0
}) => {
  const steps = [
    { icon: faExchangeAlt, text: "Switching exam preparation", color: theme?.primary?.blue?.[500] },
    { icon: faCog, text: "Loading new content", color: theme?.primary?.orange?.[500] },
    { icon: faBook, text: "Preparing study materials", color: theme?.primary?.green?.[500] },
    { icon: faClipboardList, text: "Setting up practice tests", color: theme?.primary?.blue?.[400] },
    { icon: faLayerGroup, text: "Organizing flashcards", color: theme?.primary?.orange?.[400] },
    { icon: faRocket, text: "Almost ready!", color: theme?.primary?.green?.[400] }
  ];

  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]}20, ${theme?.primary?.orange?.[600]}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.primary?.orange?.[500],
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    bundleContainer: {
      marginBottom: '2rem',
      maxHeight: 'calc(60vh - 150px)',
      overflowY: 'auto',
      paddingRight: '0.5rem',
      display: bundles.length > 5 ? 'grid' : 'block',
      gridTemplateColumns: bundles.length > 5 ? 'repeat(2, 1fr)' : 'none',
      gap: bundles.length > 5 ? '0.75rem' : '0',
    },
    bundleOption: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.875rem 1.25rem',
      marginBottom: bundles.length > 5 ? '0' : '0.75rem',
      borderRadius: '0.75rem',
      border: '0px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '0.875rem',
      fontWeight: '500',
    },
    bundleTitle: {
      color: 'white',
      fontSize: '1rem',
      fontWeight: '600',
    },
    checkIcon: {
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255, 255, 255, 0.2)',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    confirmButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]}, ${theme?.primary?.blue?.[600]})`,
      color: 'white',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    // Loader styles
    loaderContainer: {
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[600]} 0%, ${theme?.primary?.blue?.[800]} 100%)`,
      borderRadius: '1rem',
      padding: '2rem',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    loaderContent: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      borderRadius: '1.5rem',
      padding: '2rem',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      textAlign: 'center',
      position: 'relative',
      zIndex: 10,
    },
    loaderTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: 'white',
      marginBottom: '0.5rem',
    },
    loaderSubtitle: {
      fontSize: '0.875rem',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '1.5rem',
    },
    progressBar: {
      width: '100%',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '0.5rem',
      height: '0.5rem',
      overflow: 'hidden',
      marginBottom: '1rem',
    },
    progressFill: {
      height: '100%',
      background: 'white',
      borderRadius: '0.5rem',
      transition: 'width 0.3s ease',
    },
    stepContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      marginBottom: '1rem',
    },
    stepIcon: {
      fontSize: '1.25rem',
    },
    stepText: {
      color: 'white',
      fontWeight: '500',
    },
    stepIndicators: {
      display: 'flex',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    stepDot: {
      width: '0.5rem',
      height: '0.5rem',
      borderRadius: '50%',
      transition: 'all 0.3s ease',
    },
  };

  // If loading, show the loader content
  if (isLoading) {
    return (
      <BaseModal isOpen={isOpen} onClose={null} theme={theme} maxWidth="600px">
        <div style={styles.loaderContainer}>
          {/* Background Animation Elements */}
          <motion.div
            style={{
              position: 'absolute',
              top: '25%',
              left: '25%',
              width: '3rem',
              height: '3rem',
              borderRadius: '50%',
              background: theme?.primary?.orange?.[400],
              opacity: 0.2,
            }}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            style={{
              position: 'absolute',
              bottom: '25%',
              right: '25%',
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              background: theme?.primary?.green?.[400],
              opacity: 0.2,
            }}
            animate={{ 
              scale: [1, 1.4, 1],
              opacity: [0.2, 0.6, 0.2]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={styles.loaderContent}
          >
            {/* Header */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '4rem',
                height: '4rem',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                marginBottom: '1rem',
              }}
            >
              <FontAwesomeIcon 
                icon={faExchangeAlt} 
                style={{ color: 'white', fontSize: '1.5rem' }}
              />
            </motion.div>
            
            <h2 style={styles.loaderTitle}>Switching Exam</h2>
            <p style={styles.loaderSubtitle}>
              Please wait while we prepare your new study environment
            </p>

            {/* Progress Bar */}
            <div style={styles.progressBar}>
              <motion.div
                style={styles.progressFill}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Current Step */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={styles.stepContainer}
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{ 
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <FontAwesomeIcon 
                    icon={steps[currentStep]?.icon || faCog} 
                    style={{ 
                      ...styles.stepIcon,
                      color: steps[currentStep]?.color || theme?.primary?.blue?.[500]
                    }}
                  />
                </motion.div>
                <span style={styles.stepText}>
                  {steps[currentStep]?.text || "Loading..."}
                </span>
              </motion.div>
            </AnimatePresence>

            {/* Step Indicators */}
            <div style={styles.stepIndicators}>
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  style={{
                    ...styles.stepDot,
                    background: index <= currentStep ? 'white' : 'rgba(255, 255, 255, 0.3)',
                  }}
                  animate={{
                    scale: index === currentStep ? [1, 1.3, 1] : 1
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: index === currentStep ? Infinity : 0
                  }}
                />
              ))}
            </div>

            {/* Completion Message */}
            {progress >= 100 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ textAlign: 'center', marginTop: '1rem' }}
              >
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  style={{ color: '#4ade80', fontSize: '1.25rem', marginBottom: '0.5rem' }}
                />
                <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem' }}>
                  All set! Redirecting to your new exam...
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </BaseModal>
    );
  }

  // Normal modal content
  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme} maxWidth="600px">
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -10, 10, 0],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faExchangeAlt} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>{title}</h2>
      <p style={styles.description}>
        {description}
      </p>
      
      <div style={styles.bundleContainer}>
        {bundles.map((bundle, index) => (
          <motion.div
            key={bundle.key}
            style={{
              ...styles.bundleOption,
              background: theme?.primary?.blue?.[500],
              borderColor: selectedBundle === bundle.key ? 'rgba(255, 255, 255, 0.3)' : 'transparent',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onBundleSelect(bundle.key)}
          >
            <span style={styles.bundleTitle}>{bundle.exam_full_name || bundle.title}</span>
            {selectedBundle === bundle.key && (
              <div style={styles.checkIcon}>
                <FontAwesomeIcon icon={faCheck} style={{ color: 'white', fontSize: '0.75rem' }} />
              </div>
            )}
          </motion.div>
        ))}
      </div>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.confirmButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onConfirm(selectedBundle)}
        >
          Continue
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Success Guarantee Modal
export const SuccessGuaranteeModal = ({ isOpen, onClose, theme }) => {
  const styles = {
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `1px solid ${theme?.neutral?.gray?.[200]}`,
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: theme?.text?.primary,
      margin: 0,
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    sectionContent: {
      fontSize: '16px',
      lineHeight: '1.6',
      color: theme?.text?.secondary,
      marginBottom: '16px',
    },
    requirementList: {
      listStyle: 'none',
      padding: 0,
      marginLeft: '10px',
    },
    requirementItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px',
      fontSize: '16px',
      lineHeight: '1.5',
      color: theme?.text?.secondary,
    },
    requirementIcon: {
      color: theme?.primary?.blue?.[500],
      marginTop: '2px',
      flexShrink: 0,
    },
    disclaimer: {
      fontSize: '14px',
      color: theme?.text?.tertiary,
      fontStyle: 'italic',
      marginTop: '24px',
      padding: '16px',
      backgroundColor: theme?.neutral?.gray?.[50],
      borderRadius: '8px',
      border: `1px solid ${theme?.neutral?.gray?.[200]}`,
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <div style={styles.modalHeader}>
        <FontAwesomeIcon 
          icon={faShieldAlt} 
          style={{ color: theme?.primary?.blue?.[500], fontSize: '24px' }}
        />
        <h2 style={styles.modalTitle}>LearnZapp Success Guarantee</h2>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          <FontAwesomeIcon icon={faHandshake} style={styles.requirementIcon} />
          Our Commitment
        </h3>
        <p style={styles.sectionContent}>
          Study smart, pass your exam, or get 3 additional months of Premium access completely free. 
          We're confident in our content quality and want you to succeed.
        </p>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          
          How to Qualify
        </h3>
        <ul style={styles.requirementList}>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Complete all available practice questions at least once
          </li>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Achieve and maintain a readiness score of 80% or higher
          </li>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Submit your official exam results within 30 days of taking the exam
          </li>
        </ul>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
         
          How to Claim
        </h3>
        <p style={styles.sectionContent}>
          If you don't pass your exam after meeting the requirements:
        </p>
        <ul style={styles.requirementList}>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Contact our support team (support@learnzapp.com) within 30 days
          </li>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Provide your official exam results
          </li>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            Send email ID you use in the app, so we can lookup your completion and readiness scores
          </li>
          <li style={styles.requirementItem}>
            <FontAwesomeIcon icon={faCheckCircle} style={styles.requirementIcon} />
            We'll extend your Premium access for 3 months at no cost
          </li>
        </ul>
      </div>

      <div style={styles.disclaimer}>
        *Pass Promise is available to Premium subscribers only. Terms and conditions apply. 
        We reserve the right to modify guarantee terms at any time. This guarantee demonstrates 
        our confidence in helping you succeed with comprehensive preparation.
      </div>
    </BaseModal>
  );
};

